import React, { useState, useEffect, useMemo } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../supabase/supabaseClient'
import { convertOdds } from '../utils/oddsConverter'
import { BOOKMAKER_LOGOS, BOOKMAKERS_BY_REGION as BOOKMAKERS } from '../config/bookmakers'
import { ALL_SPORTS as SPORTS, eventLabel, eventLabelShort } from '../config/sports'
import { MARKET_TYPE_MAPPING, SELECTION_SHORT_NAMES } from '../config/markets'
import { saveUserBet, getRecentBetsOfType, saveUserFilter, getUserFilters, deleteUserFilter, setDefaultFilter, unsetDefaultFilter, getDefaultFilter } from '../services/userDataService'
import '../styles/app.css'
import './ArbNew.css'

import FilterPanel from './FilterPanel'
import HiddenBetsModal from './HiddenBetsModal'
import TrackBetModal from './TrackBetModal'
import ArbTour, { openArbTour } from './arbTour'
import PreviewWall from '../components/PreviewWall'
import useVisiblePolling from '../hooks/useVisiblePolling'

export default function ArbNew({ isVisible = true, isPreview: isPreviewProp = false }) {
  const { user, oddsFormat, hasAccess, hadTrial, loading: authLoading } = useAuth()
  const isPreview = isPreviewProp || authLoading || !hasAccess('arb')

  // ─── Data ────────────────────────────────────────────────────────────────
  const [opportunities, setOpportunities]           = useState([])
  const [loading, setLoading]                       = useState(true)
  const [error, setError]                           = useState(null)
  const [lastUpdate, setLastUpdate]                 = useState(null)
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false)

  // ─── Stake settings ──────────────────────────────────────────────────────
  const [totalStake, setTotalStake]   = useState(100)
  const [roundStakes, setRoundStakes] = useState('1')

  // ─── Filters ─────────────────────────────────────────────────────────────
  const [filters, setFilters] = useState({
    sports:             new Set(),
    bookmakers:         new Set(),
    requiredBookmakers: new Set(),
    odds:               [1, 10],
    profit:             [0, 50],
    hoursUntil:         [0, 72],
  })
  const [savedFilters, setSavedFilters]           = useState([])
  const [filterName, setFilterName]               = useState('')
  const [activeFilterId, setActiveFilterId]       = useState(null)
  const [defaultFilterLoaded, setDefaultFilterLoaded] = useState(false)
  const [showFilterPanel, setShowFilterPanel]     = useState(false)

  // ─── Sort + pagination ───────────────────────────────────────────────────
  const [sortBy, setSortBy]           = useState('profit_percentage')
  const [sortDir, setSortDir]         = useState('desc')
  const [currentPage, setCurrentPage] = useState(1)
  const itemsPerPage = 25

  // ─── Row state ───────────────────────────────────────────────────────────
  const [expandedRows, setExpandedRows] = useState(new Set())
  const [editableData, setEditableData] = useState({})

  // ─── Tracking ────────────────────────────────────────────────────────────
  const [trackedBets, setTrackedBets]               = useState(new Set())
  const [showTrackModal, setShowTrackModal]         = useState(false)
  const [trackingOpportunity, setTrackingOpportunity] = useState(null)
  const [trackBetForm, setTrackBetForm] = useState({
    sport: '', match: '', market_type: '',
    bookmaker: '', bookmaker2: '',
    selection1: '', selection2: '',
    stake: '', profit: '',
    date: new Date().toISOString().split('T')[0],
  })

  // ─── Hidden bets ─────────────────────────────────────────────────────────
  const [hiddenBets, setHiddenBets]         = useState({})
  const [showHiddenModal, setShowHiddenModal] = useState(false)

  // ─── Modals ──────────────────────────────────────────────────────────────

  // ─── Live-dot tick ───────────────────────────────────────────────────────
  // eslint-disable-next-line no-unused-vars
  const [tick, setTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 10000)
    return () => clearInterval(id)
  }, [])

  const dataAgeSec = lastUpdate ? (new Date() - lastUpdate) / 1000 : null
  const dotClass   = !lastUpdate ? 'bc-live-dot' : dataAgeSec < 60 ? 'bc-live-dot' : 'bc-live-dot stale'

  const formatTimeWithSeconds = (dateString) => {
    if (!dateString) return ''
    return new Date(dateString + 'Z').toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit', hour12: true })
  }

  // ─── Fetch ────────────────────────────────────────────────────────────────
  const fetchOpportunities = async (silent = false) => {
    try {
      if (!silent) setLoading(true)
      const { data, error } = await supabase.functions.invoke('arb-proxy')
      if (error) throw error
      setOpportunities(data.opportunities || [])
      setLastUpdate(new Date())
      setError(null)
    } catch (err) {
      setError(err.message)
    } finally {
      if (!silent) setLoading(false)
    }
  }

  useEffect(() => { fetchOpportunities() }, [])

  useVisiblePolling(() => fetchOpportunities(true), 30000, autoRefreshEnabled && isVisible)

  // ─── Hidden bets bootstrap ───────────────────────────────────────────────
  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem('hidden-arb-bets') || '{}')
    const now = new Date()
    const cleaned = {}
    Object.entries(stored).forEach(([id, data]) => {
      const gameTime = new Date(data.commence_time + 'Z')
      if (now < new Date(gameTime.getTime() + 3600000)) cleaned[id] = data
    })
    localStorage.setItem('hidden-arb-bets', JSON.stringify(cleaned))
    setHiddenBets(cleaned)
  }, [])

  // ─── Filter persistence ──────────────────────────────────────────────────
  useEffect(() => {
    if (!user?.id) {
      setFilters(prev => ({ ...prev, sports: new Set(SPORTS), bookmakers: new Set(BOOKMAKERS.AU) }))
      return
    }
    getUserFilters(user.id, 'arb').then(({ data }) => {
      if (data) setSavedFilters(data.map(f => ({ id: f.id, name: f.filter_name, isDefault: f.is_default || false, ...f.filter_data })))
    })
  }, [user?.id])

  useEffect(() => {
    if (!user?.id || defaultFilterLoaded) return
    getDefaultFilter(user.id, 'arb').then(({ data }) => {
      if (data?.filter_data) {
        applyFilterData(data.filter_data)
      } else {
        setFilters(prev => ({ ...prev, sports: new Set(SPORTS), bookmakers: new Set(BOOKMAKERS.AU) }))
      }
      setDefaultFilterLoaded(true)
    })
  }, [user?.id, defaultFilterLoaded])

  const applyFilterData = (fd) => {
    // Prefer the saved excludedSports blacklist (newer filters) so leagues added after the
    // filter was saved stay visible (selected = all minus excluded). Fall back to the legacy
    // sports whitelist for old filters.
    const selectedSports = Array.isArray(fd.filters.excludedSports)
      ? SPORTS.filter(s => !fd.filters.excludedSports.includes(s))
      : (fd.filters.sports || SPORTS)
    setFilters({
      sports:             new Set(selectedSports),
      bookmakers:         new Set(fd.filters.bookmakers || BOOKMAKERS.AU),
      requiredBookmakers: new Set(fd.filters.requiredBookmakers || []),
      odds:       fd.filters.odds       || [1, 10],
      profit:     fd.filters.profit     || [0, 50],
      hoursUntil: fd.filters.hoursUntil || [0, 72],
    })
    if (fd.stakeSettings) {
      setTotalStake(fd.stakeSettings.totalStake ?? 100)
      setRoundStakes(fd.stakeSettings.roundStakes ?? '1')
    }
  }

  const handleSaveFilter = async (name) => {
    if (!name.trim() || !user?.id) return
    const fd = {
      filters: {
        sports:             Array.from(filters.sports),                       // legacy whitelist (back-compat)
        excludedSports:     SPORTS.filter(s => !filters.sports.has(s)),       // blacklist = source of truth on load
        bookmakers:         Array.from(filters.bookmakers),
        requiredBookmakers: Array.from(filters.requiredBookmakers),
        odds:       filters.odds,
        profit:     filters.profit,
        hoursUntil: filters.hoursUntil,
      },
      stakeSettings: { totalStake, roundStakes },
      createdAt: new Date().toISOString(),
    }
    const { data, error } = await saveUserFilter(user.id, 'arb', name.trim(), fd)
    if (error) { alert('Error saving filter: ' + error); return }
    if (data) setSavedFilters(prev => [...prev, { id: data.id, name: data.filter_name, isDefault: false, ...data.filter_data }])
    setFilterName('')
  }

  const handleLoadFilter = (saved) => {
    applyFilterData(saved)
    setActiveFilterId(saved.id)
  }

  const handleDeleteFilter = async (id) => {
    if (!user?.id) return
    const { error } = await deleteUserFilter(id)
    if (error) { alert('Error deleting filter: ' + error); return }
    setSavedFilters(prev => prev.filter(f => f.id !== id))
    if (activeFilterId === id) setActiveFilterId(null)
  }

  const handleToggleDefault = async (id) => {
    if (!user?.id) return
    const current = savedFilters.find(f => f.id === id)
    if (current?.isDefault) {
      const { error } = await unsetDefaultFilter(user.id, 'arb', id)
      if (error) { alert('Error unsetting default: ' + error); return }
    } else {
      const { error } = await setDefaultFilter(user.id, 'arb', id)
      if (error) { alert('Error setting default: ' + error); return }
    }
    setSavedFilters(prev => prev.map(f => ({ ...f, isDefault: f.id === id ? !current?.isDefault : false })))
  }

  const resetFilters = () => {
    setFilters({ sports: new Set(SPORTS), bookmakers: new Set(BOOKMAKERS.AU), requiredBookmakers: new Set(), odds: [1, 10], profit: [1, 50], hoursUntil: [1, 24] })
    setTotalStake(100)
    setRoundStakes('1')
  }

  // ─── Arb logic ───────────────────────────────────────────────────────────
  const calculateArbStakes = (outcomes, customOdds = null, customStakeTotal = null) => {
    if (!outcomes || outcomes.length !== 2) return { stakes: [], profit: 0, totalStaked: 0, individualProfits: [] }
    const [o1, o2] = outcomes
    const odds1 = customOdds?.[0] ?? o1.odds
    const odds2 = customOdds?.[1] ?? o2.odds
    const stakeToUse = customStakeTotal ?? totalStake
    const totalImplied = (1 / odds1) + (1 / odds2)
    const raw1 = stakeToUse / (odds1 * totalImplied)
    const raw2 = stakeToUse / (odds2 * totalImplied)
    const round = (v) => {
      if (roundStakes === 'none') return Math.round(v * 100) / 100
      if (roundStakes === '1')    return Math.round(v)
      if (roundStakes === '5')    return Math.round(v / 5) * 5
      return v
    }
    const s1 = round(raw1)
    const s2 = round(raw2)
    const p1 = Math.round(((s1 * odds1) - (s1 + s2)) * 100) / 100
    const p2 = Math.round(((s2 * odds2) - (s1 + s2)) * 100) / 100
    return { stakes: [s1, s2], profit: Math.round(((p1 + p2) / 2) * 100) / 100, totalStaked: s1 + s2, individualProfits: [p1, p2] }
  }

  const profitBadgeClass = (p) => p >= 10 ? 'bc-ev-badge bc-ev-high' : p >= 5 ? 'bc-ev-badge bc-ev-mid' : 'bc-ev-badge bc-ev-low'

  const getEditableOdds = (oppId, idx, defaultVal) => {
    const d = editableData[oppId]
    return d?.odds?.[idx] !== undefined ? d.odds[idx] : defaultVal
  }

  const getEditableStake = (oppId, defaultVal) => {
    const d = editableData[oppId]
    return d?.totalStake !== undefined ? d.totalStake : defaultVal
  }

  const updateOdds = (oppId, idx, val) => {
    setEditableData(prev => {
      const d = { ...(prev[oppId] || {}) }
      d.odds = [...(d.odds || [])]
      d.oddsRaw = [...(d.oddsRaw || [])]
      d.oddsRaw[idx] = val
      const num = parseFloat(val)
      d.odds[idx] = isNaN(num) ? d.odds[idx] : num
      return { ...prev, [oppId]: d }
    })
  }

  const getEditableOddsRaw = (oppId, idx, defaultVal) => {
    const d = editableData[oppId]
    return d?.oddsRaw?.[idx] !== undefined ? d.oddsRaw[idx] : String(defaultVal)
  }

  const updateStake = (oppId, val) => {
    setEditableData(prev => {
      const num = parseFloat(val)
      return { ...prev, [oppId]: { ...(prev[oppId] || {}), totalStakeRaw: val, totalStake: isNaN(num) ? (prev[oppId]?.totalStake ?? totalStake) : num } }
    })
  }

  const getEditableStakeRaw = (oppId, defaultVal) => {
    const d = editableData[oppId]
    return d?.totalStakeRaw !== undefined ? d.totalStakeRaw : String(defaultVal)
  }

  // ─── Format helpers ──────────────────────────────────────────────────────
  const formatDateTime = (ds) => new Date(ds + 'Z').toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
  const getHoursUntil  = (ct) => Math.max(0, (new Date(ct + 'Z') - new Date()) / 3600000)

  const formatMarketType = (marketType, playerName = '') => {
    if (MARKET_TYPE_MAPPING[marketType]) return MARKET_TYPE_MAPPING[marketType]
    if (playerName && marketType.includes('player_')) {
      return `Player Prop - ${marketType.replace('player_', '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}`
    }
    return marketType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  const formatSelection = (selection, marketType, playerName = '') => {
    if (SELECTION_SHORT_NAMES[marketType]) {
      const parts = selection.split(' ')
      for (let i = 0; i < parts.length; i++) {
        if ((parts[i].toLowerCase() === 'over' || parts[i].toLowerCase() === 'under') && i + 1 < parts.length && parts[i + 1].replace('.', '').replace('-', '').match(/^\d+$/)) {
          return playerName
            ? `${playerName} ${parts[i]} ${parts[i + 1]} ${SELECTION_SHORT_NAMES[marketType]}`
            : `${parts[i]} ${parts[i + 1]} ${SELECTION_SHORT_NAMES[marketType]}`
        }
      }
    }
    if (marketType === 'alternate_lines') {
      const parts = selection.split(' ')
      if (parts.length >= 2) {
        const team = parts.slice(0, -1).join(' ')
        let hcap = parts[parts.length - 1]
        if (hcap[0] !== '-' && hcap[0] !== '+') hcap = `+${hcap}`
        return `${team} ${hcap}`
      }
    }
    return selection
  }

  // ─── Sort ────────────────────────────────────────────────────────────────
  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'desc' ? 'asc' : 'desc')
    else { setSortBy(col); setSortDir('desc') }
  }

  // ─── Filter + sort opportunities ─────────────────────────────────────────
  const filteredOpportunities = useMemo(() => {
    const filtered = opportunities.filter(opp => {
      if (filters.sports.size > 0 && !filters.sports.has(opp.sport)) return false
      if (opp.outcomes && !opp.outcomes.every(o => filters.bookmakers.has(o.bookmaker))) return false
      if (filters.requiredBookmakers?.size > 0 && opp.outcomes &&
          !opp.outcomes.some(o => filters.requiredBookmakers.has(o.bookmaker))) return false
      if (opp.outcomes && opp.outcomes.some(o => o.odds < filters.odds[0] || o.odds > filters.odds[1])) return false
      if (opp.profit_percentage < filters.profit[0] || opp.profit_percentage > filters.profit[1]) return false
      const hrs = getHoursUntil(opp.commence_time)
      if (hrs < filters.hoursUntil[0] || hrs > filters.hoursUntil[1]) return false
      return true
    })

    return filtered.sort((a, b) => {
      let av, bv
      if (sortBy === 'profit_percentage') { av = a.profit_percentage; bv = b.profit_percentage }
      else if (sortBy === 'odds') {
        av = a.outcomes ? a.outcomes.reduce((s, o) => s + o.odds, 0) / a.outcomes.length : 0
        bv = b.outcomes ? b.outcomes.reduce((s, o) => s + o.odds, 0) / b.outcomes.length : 0
      } else if (sortBy === 'start_time') {
        av = new Date(a.commence_time).getTime()
        bv = new Date(b.commence_time).getTime()
      } else if (sortBy === 'sport') {
        return sortDir === 'desc' ? b.sport.localeCompare(a.sport) : a.sport.localeCompare(b.sport)
      } else { av = 0; bv = 0 }
      return sortDir === 'desc' ? bv - av : av - bv
    })
  }, [opportunities, filters, sortBy, sortDir])

  const visibleOpportunities = useMemo(
    () => filteredOpportunities.filter(opp => !hiddenBets[generateHideId(opp)]),
    [filteredOpportunities, hiddenBets]
  )

  const totalPages = Math.max(1, Math.ceil(visibleOpportunities.length / itemsPerPage))
  useEffect(() => { if (currentPage > totalPages) setCurrentPage(1) }, [totalPages, currentPage])
  useEffect(() => { setCurrentPage(1) }, [filters, sortBy, sortDir])

  const pagedOpps = isPreview
    ? visibleOpportunities.slice(0, 3)
    : visibleOpportunities.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)

  // ─── Hidden bets ─────────────────────────────────────────────────────────
  function generateHideId(opp) {
    const s1 = opp.outcomes?.[0]?.selection || ''
    const s2 = opp.outcomes?.[1]?.selection || ''
    const b1 = opp.outcomes?.[0]?.bookmaker || ''
    const b2 = opp.outcomes?.[1]?.bookmaker || ''
    return `${opp.sport}_${opp.match}_${opp.market_type}_${s1}_${s2}_${b1}_${b2}`.replace(/[^a-zA-Z0-9_]/g, '_')
  }

  const hideOpportunity = (opp) => {
    const id = generateHideId(opp)
    const updated = {
      ...hiddenBets,
      [id]: { match: opp.match, selection1: opp.outcomes?.[0]?.selection, selection2: opp.outcomes?.[1]?.selection, bookmaker1: opp.outcomes?.[0]?.bookmaker, bookmaker2: opp.outcomes?.[1]?.bookmaker, commence_time: opp.commence_time, sport: opp.sport, market_type: opp.market_type },
    }
    localStorage.setItem('hidden-arb-bets', JSON.stringify(updated))
    setHiddenBets(updated)
  }

  const unhideOpportunity = (betId) => {
    const updated = { ...hiddenBets }
    delete updated[betId]
    localStorage.setItem('hidden-arb-bets', JSON.stringify(updated))
    setHiddenBets(updated)
  }

  const clearAllHidden = () => {
    localStorage.removeItem('hidden-arb-bets')
    setHiddenBets({})
  }

  // ─── Track bets ──────────────────────────────────────────────────────────
  const generateBetId = (opp) => {
    const b1 = opp.outcomes?.[0]?.bookmaker || ''
    const b2 = opp.outcomes?.[1]?.bookmaker || ''
    const s1 = opp.outcomes?.[0]?.selection || ''
    const s2 = opp.outcomes?.[1]?.selection || ''
    return `${opp.sport}_${opp.match}_${opp.market_type || ''}_${s1}_${s2}_${b1}_${b2}`.replace(/[^a-zA-Z0-9_]/g, '_')
  }

  const isTracked = (opp) => trackedBets.has(generateBetId(opp))

  // Once per user, not per opportunities refresh: a save adds its own id to
  // the set, so refetching on every poll only re-read the same bets. Reads
  // the last week of this type rather than the 15 most recent bets of any
  // type, which let older tracked opportunities lose their tick.
  useEffect(() => {
    if (!user?.id) return
    getRecentBetsOfType(user.id, 'ARB').then(({ data }) => {
      if (!data) return
      const ids = data.map(b => {
        const d = b.bet_data
        return `${d.sport}_${d.match}_${d.market_type || ''}_${d.selection1 || ''}_${d.selection2 || ''}_${d.bookmaker || ''}_${d.bookmaker2 || ''}`.replace(/[^a-zA-Z0-9_]/g, '_')
      })
      setTrackedBets(new Set(ids))
    })
  }, [user?.id])

  const openTrackModal = (opp) => {
    setTrackingOpportunity(opp)
    setTrackBetForm({
      sport:       opp.sport,
      match:       opp.match,
      market_type: opp.market_type,
      bookmaker:   opp.outcomes?.[0]?.bookmaker || '',
      bookmaker2:  opp.outcomes?.[1]?.bookmaker || '',
      selection1:  opp.outcomes?.[0]?.selection || '',
      selection2:  opp.outcomes?.[1]?.selection || '',
      stake:       totalStake,
      profit:      '',
      date:        new Date(opp.commence_time + 'Z').toLocaleDateString('en-CA'),
    })
    setShowTrackModal(true)
  }

  const closeTrackModal = () => {
    setShowTrackModal(false)
    setTrackingOpportunity(null)
    setTrackBetForm({ sport: '', match: '', market_type: '', bookmaker: '', bookmaker2: '', selection1: '', selection2: '', stake: '', profit: '', date: new Date().toISOString().split('T')[0] })
  }

  const saveTrack = async (andHide = false) => {
    if (!user?.id) { alert('Please log in to track bets'); return }
    const newBet = {
      type:        'ARB',
      sport:       trackBetForm.sport,
      match:       trackBetForm.match,
      market_type: trackBetForm.market_type,
      bookmaker:   trackBetForm.bookmaker,
      bookmaker2:  trackBetForm.bookmaker2,
      selection1:  trackBetForm.selection1,
      selection2:  trackBetForm.selection2,
      stake:       parseFloat(trackBetForm.stake),
      status:      trackBetForm.profit ? (parseFloat(trackBetForm.profit) > 0 ? 'won' : 'lost') : 'pending',
      actual_profit: trackBetForm.profit ? parseFloat(trackBetForm.profit) : null,
      // Placed is the moment it was tracked; the game's own time is
      // commence_time, which the tracker shows as the bet's date. A profit
      // entered here settles it at that game time.
      commence_time: trackingOpportunity?.commence_time ?? null,
      placed_at: new Date().toISOString(),
      settled_at: trackBetForm.profit
        ? (trackingOpportunity?.commence_time ? new Date(trackingOpportunity.commence_time + 'Z') : new Date()).toISOString()
        : null,
    }
    const { error } = await saveUserBet(user.id, newBet)
    if (error) { alert('Error saving bet. Please try again.'); return }
    const betId = generateBetId(trackingOpportunity)
    setTrackedBets(prev => new Set([...prev, betId]))
    if (andHide) hideOpportunity(trackingOpportunity)
    closeTrackModal()
  }

  // ─── Row expand ──────────────────────────────────────────────────────────
  const toggleExpand = (id) => setExpandedRows(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  if (!isVisible) return null

  const hiddenCount = Object.keys(hiddenBets).length

  return (
    <div className="bc-container tool-unified tool-refined arb-unified">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="bc-header">
        <div className="bc-header-titlewrap">
          <div className="bc-header-icon" aria-hidden="true">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="17 1 21 5 17 9"/>
              <path d="M3 11V9a4 4 0 0 1 4-4h14"/>
              <polyline points="7 23 3 19 7 15"/>
              <path d="M21 13v2a4 4 0 0 1-4 4H3"/>
            </svg>
          </div>
          <div className="bc-header-textcol">
            <div className="bc-header-titlerow">
              <h1 className="bc-title">Arbitrage</h1>
              <button className="bc-info-btn" onClick={openArbTour} aria-label="How this works" data-tooltip="How this works">?</button>
              <span
                className={dotClass}
                data-tooltip={lastUpdate ? `Last refreshed ${formatTimeWithSeconds(lastUpdate.toISOString().replace('Z', ''))}` : 'No data yet'}
              />
            </div>
          </div>
        </div>

        <div className="bc-header-actions">
          <button className="bc-btn" onClick={() => setShowFilterPanel(true)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
            </svg>
            Filters
          </button>

          {hiddenCount > 0 && (
            <button className="bc-btn" onClick={() => setShowHiddenModal(true)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                <line x1="1" y1="1" x2="23" y2="23"/>
              </svg>
              {hiddenCount}
            </button>
          )}

          <button
            className={`bc-btn bc-btn-toggle${autoRefreshEnabled ? ' toggle-active' : ''}`}
            onClick={() => setAutoRefreshEnabled(v => !v)}
          >
            Auto
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
          </button>

          <button className="bc-btn bc-icon-btn" onClick={() => fetchOpportunities()} disabled={loading}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={loading ? 'bc-spin' : ''}>
              <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
          </button>
        </div>
      </div>

      {error && <div className="bc-error">Error: {error}</div>}

      {loading && (
        <div className="bc-loading">
          <div className="bc-spinner" />
        </div>
      )}

      {!loading && (
        <div className="bc-table-wrapper">
          <div className="bc-table-scroll">
            <div className="arb-grid-table">

              {/* ── Header strip ─────────────────────────────────────────── */}
              <div className="arb-grid-header">
                {[
                  { label: 'Match',   key: 'start_time', divider: false },
                  { label: 'Market',  key: null,         divider: false },
                  { label: 'Outcomes', key: 'odds',              divider: 'right' },
                  { label: 'Stakes',  key: null,                 divider: false   },
                  { label: 'Profit%', key: 'profit_percentage',  divider: false   },
                  { label: '',        key: null,                 divider: false   },
                ].map((col, i) => (
                  <div
                    key={i}
                    className={`arb-grid-header-cell${col.key ? ' sortable' : ''}${sortBy === col.key ? ' sorted' : ''}${col.divider === 'right' ? ' divider-right' : ''}`}
                    onClick={() => col.key && handleSort(col.key)}
                  >
                    {col.label}
                    {col.key && (
                      <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        {sortBy === col.key
                          ? sortDir === 'desc' ? <polyline points="6 9 12 15 18 9"/> : <polyline points="18 15 12 9 6 15"/>
                          : <><polyline points="6 9 12 4 18 9"/><polyline points="6 15 12 20 18 15"/></>
                        }
                      </svg>
                    )}
                  </div>
                ))}
              </div>

              {/* ── Rows ─────────────────────────────────────────────────── */}
              <div className="arb-grid-rows">
                {pagedOpps.map((opp, idx) => {
                  const rowId      = opp._id
                  const isExpanded = expandedRows.has(rowId)
                  const blurRow    = isPreview && idx > 0
                  const blurStyle  = blurRow ? { filter: 'blur(5px)', userSelect: 'none', pointerEvents: 'none' } : {}
                  const blurDetail = isPreview && idx === 0

                  const customOdds  = [getEditableOdds(rowId, 0, opp.outcomes?.[0]?.odds ?? 0), getEditableOdds(rowId, 1, opp.outcomes?.[1]?.odds ?? 0)]
                  const customStake = getEditableStake(rowId, totalStake)
                  const arbCalc     = calculateArbStakes(opp.outcomes, customOdds, customStake)

                  return (
                    <div key={rowId} className={`arb-grid-row-wrap${isExpanded ? ' expanded' : ''}`}>

                      {/* Main row */}
                      <div className="arb-grid-row" style={blurStyle}>

                        {/* Match, then sport tag + kick-off beneath it */}
                        <div className="arb-grid-cell">
                          <div className="arb-match-name">{opp.match}</div>
                          <div className="bc-match-meta">
                            <span className="bc-sport-tag" title={eventLabel(opp)}>{eventLabelShort(opp)}</span>
                            <span className="arb-match-time">{formatDateTime(opp.commence_time)}</span>
                          </div>
                        </div>

                        {/* Market */}
                        <div className="arb-grid-cell">
                          <span className="arb-market-text">{formatMarketType(opp.market_type, opp.player_name)}</span>
                        </div>

                        {/* Outcomes */}
                        <div className="arb-grid-cell divider-right" style={blurDetail ? { filter: 'blur(6px)', userSelect: 'none', pointerEvents: 'none' } : {}}>
                          <div className="arb-outcomes-box">
                          {opp.outcomes?.map((outcome, oi) => {
                            const logo = BOOKMAKER_LOGOS[outcome.bookmaker]
                            const abbr = outcome.bookmaker.slice(0, 2).toUpperCase()
                            return (
                              <div key={oi} className="arb-leg-row">
                                <span className="arb-leg-sel">{formatSelection(outcome.selection, opp.market_type, opp.player_name)}</span>
                                <div className="arb-leg-book-odds">
                                  <span
                                    className="arb-leg-book"
                                    data-tooltip={outcome.bookmaker}
                                    onClick={() => outcome.game_url && window.open(outcome.game_url, '_blank')}
                                    style={{ cursor: outcome.game_url ? 'pointer' : 'default' }}
                                  >
                                    {logo
                                      ? <img src={logo} alt={abbr} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
                                      : abbr
                                    }
                                  </span>
                                  <span className="arb-leg-odds">{convertOdds(customOdds[oi], oddsFormat)}</span>
                                </div>
                              </div>
                            )
                          })}
                          </div>
                        </div>

                        {/* Stakes */}
                        <div className="arb-grid-cell stakes-cell" style={blurStyle}>
                          {arbCalc.stakes.map((stake, si) => (
                            <div key={si} className="arb-stake-val">${stake}</div>
                          ))}
                        </div>

                        {/* Profit% */}
                        <div className="arb-grid-cell" style={blurStyle}>
                          <span className={profitBadgeClass(opp.profit_percentage)}>
                            {opp.profit_percentage.toFixed(2)}%
                          </span>
                        </div>

                        {/* Actions */}
                        <div className="arb-grid-cell arb-actions-cell" style={blurStyle}>
                          {!isPreview && (
                            <button
                              className={`arb-action-btn track-btn${isTracked(opp) ? ' tracked' : ''}`}
                              onClick={() => !isTracked(opp) && openTrackModal(opp)}
                              data-tooltip={isTracked(opp) ? 'Tracked' : 'Track bet'}
                            >
                              {isTracked(opp)
                                ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                              }
                            </button>
                          )}
                          {!isPreview && (
                            <button
                              className="arb-action-btn hide-btn"
                              onClick={() => hideOpportunity(opp)}
                              data-tooltip="Hide"
                            >
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                                <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                                <line x1="1" y1="1" x2="23" y2="23"/>
                              </svg>
                            </button>
                          )}
                          <button
                            className={`arb-action-btn expand-btn${isPreview ? ' locked' : ''}${isExpanded ? ' active' : ''}`}
                            onClick={() => !isPreview && toggleExpand(rowId)}
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              {isExpanded ? <polyline points="18 15 12 9 6 15"/> : <polyline points="6 9 12 15 18 9"/>}
                            </svg>
                          </button>
                        </div>
                      </div>

                      {/* ── Expanded panel ──────────────────────────────── */}
                      {isExpanded && (
                        <div className="arb-expanded-panel">

                          {/* Stake top bar */}
                          <div className="arb-exp-topbar">
                            <div className="arb-exp-topbar-stake">
                              <span className="arb-exp-topbar-label">Total Stake</span>
                              <div className="arb-exp-topbar-input-wrap">
                                <span className="arb-exp-topbar-prefix">$</span>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={getEditableStakeRaw(rowId, totalStake)}
                                  onChange={e => updateStake(rowId, e.target.value)}
                                  className="arb-exp-stake-input"
                                />
                              </div>
                            </div>
                          </div>

                          {/* Outcome cards */}
                          <div className="arb-outcome-cards-row">
                          {opp.outcomes?.map((outcome, oi) => {
                            const logo = BOOKMAKER_LOGOS[outcome.bookmaker]
                            const profit = arbCalc.individualProfits[oi]
                            return (
                              <div key={oi} className="arb-outcome-card">
                                <div className="arb-outcome-card-header">
                                  {logo
                                    ? <img src={logo} alt={outcome.bookmaker} className="arb-outcome-card-logo" />
                                    : <div className="arb-outcome-card-logo-placeholder">{outcome.bookmaker.slice(0, 3).toUpperCase()}</div>
                                  }
                                  <div>
                                    <div className="arb-outcome-card-name">{outcome.bookmaker}</div>
                                    <div className="arb-outcome-card-sel">{formatSelection(outcome.selection, opp.market_type, opp.player_name)}</div>
                                  </div>
                                </div>

                                <div className="arb-outcome-field">
                                  <span className="arb-outcome-field-label">Odds</span>
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    value={getEditableOddsRaw(rowId, oi, opp.outcomes?.[oi]?.odds ?? 0)}
                                    onChange={e => updateOdds(rowId, oi, e.target.value)}
                                    className="arb-outcome-odds-input"
                                  />
                                  {oddsFormat !== 'decimal' && customOdds[oi] > 0 && (
                                    <span style={{ fontSize: '11px', color: '#64748b' }}>({convertOdds(customOdds[oi], oddsFormat)})</span>
                                  )}
                                </div>

                                <div className="arb-outcome-field">
                                  <span className="arb-outcome-field-label">Stake</span>
                                  <span className="arb-outcome-field-val">${arbCalc.stakes[oi] ?? '—'}</span>
                                </div>

                                <div className="arb-outcome-field">
                                  <span className="arb-outcome-field-label">If wins</span>
                                  <span className={typeof profit === 'number' && profit >= 0 ? 'arb-outcome-field-profit-pos' : 'arb-outcome-field-profit-neg'}>
                                    {typeof profit === 'number' ? `$${profit}` : '—'}
                                  </span>
                                </div>

                                <button className="arb-open-btn" onClick={() => outcome.game_url && window.open(outcome.game_url, '_blank')} disabled={!outcome.game_url}>
                                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                                    <polyline points="15 3 21 3 21 9"/>
                                    <line x1="10" y1="14" x2="21" y2="3"/>
                                  </svg>
                                  Open {outcome.bookmaker}
                                </button>
                              </div>
                            )
                          })}
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Preview fade */}
            {isPreview && <div className="bc-preview-fade" />}
          </div>

          {/* Preview wall */}
          {isPreview && (
            <div style={{ padding: '28px 16px 36px', display: 'flex', justifyContent: 'center' }}>
              <div style={{ width: '100%', maxWidth: '520px' }}>
                <PreviewWall
                  title={visibleOpportunities.length > 0 ? `${visibleOpportunities.length} arbitrage opportunities right now` : 'Arbitrage opportunities right now'}
                  features={['Real time odds from all major bookmakers', 'Automatic stake calculation', 'Direct links to place your bets', 'Much more...',]}
                  planName="Sports Premium"
                  accent="#60a5fa"
                  hadTrial={hadTrial}
                />
              </div>
            </div>
          )}

          {!isPreview && !loading && visibleOpportunities.length === 0 && (
            <div className="bc-empty">No arbitrage opportunities match your filters.</div>
          )}
        </div>
      )}

      {/* ── Pagination ─────────────────────────────────────────────────── */}
      {!isPreview && !loading && totalPages > 1 && (
        <div className="bc-pagination">
          <button className="bc-page-arrow" onClick={() => { setCurrentPage(1); window.scrollTo({ top: 0 }) }} disabled={currentPage === 1} data-tooltip="First page">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="11 18 5 12 11 6"/><polyline points="19 18 13 12 19 6"/></svg>
          </button>
          <button className="bc-page-arrow" onClick={() => { setCurrentPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0 }) }} disabled={currentPage === 1} data-tooltip="Previous page">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <span className="bc-page-info">{currentPage} / {totalPages}</span>
          <button className="bc-page-arrow" onClick={() => { setCurrentPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0 }) }} disabled={currentPage === totalPages} data-tooltip="Next page">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
          <button className="bc-page-arrow" onClick={() => { setCurrentPage(totalPages); window.scrollTo({ top: 0 }) }} disabled={currentPage === totalPages} data-tooltip="Last page">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="5 18 11 12 5 6"/><polyline points="13 18 19 12 13 6"/></svg>
          </button>
        </div>
      )}

      {/* ── Modals (stubs — real components added in next step) ────────── */}
      <FilterPanel
        isOpen={showFilterPanel}
        onClose={() => setShowFilterPanel(false)}
        filters={filters}
        setFilters={setFilters}
        totalStake={totalStake}
        setTotalStake={setTotalStake}
        roundStakes={roundStakes}
        setRoundStakes={setRoundStakes}
        savedFilters={savedFilters}
        activeFilterId={activeFilterId}
        filterName={filterName}
        setFilterName={setFilterName}
        onSaveFilter={handleSaveFilter}
        onLoadFilter={handleLoadFilter}
        onDeleteFilter={handleDeleteFilter}
        onToggleDefault={handleToggleDefault}
        onReset={resetFilters}
      />

      <HiddenBetsModal
        isOpen={showHiddenModal}
        hiddenBets={hiddenBets}
        onUnhide={unhideOpportunity}
        onUnhideAll={clearAllHidden}
        onClose={() => setShowHiddenModal(false)}
      />

      <TrackBetModal
        isOpen={showTrackModal}
        opp={trackingOpportunity}
        form={trackBetForm}
        onFormChange={setTrackBetForm}
        onConfirm={() => saveTrack(false)}
        onConfirmAndHide={() => saveTrack(true)}
        onClose={closeTrackModal}
      />

      <ArbTour isVisible={isVisible} isMobile={false} />
    </div>
  )
}
